import { useState, useEffect } from 'react';
import {
    ComponentsAPI,
    Component,
    ComponentsMetrics,
    FindComponentsOptions,
    QueryOptions,
    Pagination
} from '../api';

export function useComponents(options?: Partial<FindComponentsOptions>) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<Pagination>();

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getAll(options);

            if (response.code === '200' && response.data) {
  
                setData(response.data as any);

                setPagination({
                    totalItems: response.totalNoOfItem || 0,
                    page: response.page || 1,
                    perPage: response.perPage || 10,
                });
            } else {
                setError(response.message || 'Failed to fetch components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComponents();
    }, [options?.workgroupId, options?.projectIds, options?.applicationIds, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        pagination,
        refetch,
    };
}

export function useWorkgroupComponents(workgroupId: number, options?: QueryOptions) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getByWorkgroup(workgroupId, options);

            if (response.code === '200' && response.data) {
                setData(response.data as Component[]);
                setTotalItems(response.totalNoOfItem || 0);
            } else {
                setError(response.message || 'Failed to fetch workgroup components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (workgroupId) {
            fetchComponents();
        }
    }, [workgroupId, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        totalItems,
        refetch,
    };
}

export function useVulnerableComponents(workgroupId: number, options?: QueryOptions) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getVulnerable(workgroupId, options);

            if (response.code === '200' && response.data) {
                setData(response.data as Component[]);
                setTotalItems(response.totalNoOfItem || 0);
            } else {
                setError(response.message || 'Failed to fetch vulnerable components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (workgroupId) {
            fetchComponents();
        }
    }, [workgroupId, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        totalItems,
        refetch,
    };
}

export function useOutdatedComponents(workgroupId: number, options?: QueryOptions) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getOutdated(workgroupId, options);

            if (response.code === '200' && response.data) {
                setData(response.data as Component[]);
                setTotalItems(response.totalNoOfItem || 0);
            } else {
                setError(response.message || 'Failed to fetch outdated components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (workgroupId) {
            fetchComponents();
        }
    }, [workgroupId, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        totalItems,
        refetch,
    };
}

export function useDeprecatedComponents(workgroupId: number, options?: QueryOptions) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getDeprecated(workgroupId, options);

            if (response.code === '200' && response.data) {
                setData(response.data as Component[]);
                setTotalItems(response.totalNoOfItem || 0);
            } else {
                setError(response.message || 'Failed to fetch deprecated components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (workgroupId) {
            fetchComponents();
        }
    }, [workgroupId, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        totalItems,
        refetch,
    };
}

export function useScanComponents(scanId: number, options?: QueryOptions) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getByScan(scanId, options);

            if (response.code === '200' && response.data) {
                setData(response.data as Component[]);
                setTotalItems(response.totalNoOfItem || 0);
            } else {
                setError(response.message || 'Failed to fetch scan components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (scanId) {
            fetchComponents();
        }
    }, [scanId, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        totalItems,
        refetch,
    };
}

export function useProjectComponents(projectId: number, options?: QueryOptions) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getByProject(projectId, options);

            if (response.code === '200' && response.data) {
                setData(response.data as Component[]);
                setTotalItems(response.totalNoOfItem || 0);
            } else {
                setError(response.message || 'Failed to fetch project components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) {
            fetchComponents();
        }
    }, [projectId, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        totalItems,
        refetch,
    };
}

export function useApplicationComponents(applicationId: number, options?: QueryOptions) {
    const [data, setData] = useState<Component[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchComponents = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getByApplication(applicationId, options);

            if (response.code === '200' && response.data) {
                setData(response.data as Component[]);
                setTotalItems(response.totalNoOfItem || 0);
            } else {
                setError(response.message || 'Failed to fetch application components');
                setData([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (applicationId) {
            fetchComponents();
        }
    }, [applicationId, options?.page, options?.searchText]);

    const refetch = () => {
        fetchComponents();
    };

    return {
        data,
        loading,
        error,
        totalItems,
        refetch,
    };
}

export function useComponent(componentId: number) {
    const [data, setData] = useState<Component | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchComponent = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ComponentsAPI.getById(componentId);

            if (response.code === '200' && response.data) {
                setData(response.data);
            } else {
                setError(response.message || 'Failed to fetch component');
                setData(null);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (componentId) {
            fetchComponent();
        }
    }, [componentId]);

    const refetch = () => {
        fetchComponent();
    };

    return {
        data,
        loading,
        error,
        refetch,
    };
}
